import { HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { forkJoin, Observable, of, throwError } from 'rxjs';
import { catchError, map, shareReplay, switchMap, tap } from 'rxjs/operators';

import {
    ApiFragment,
    ApiQuery,
    ApiQueryBuilder,
    ApplicationConfigService,
    ClassificationMetadata,
    Combine,
    CustomHttpParamEncoder,
    DeleteStatus,
    Identifier,
    MediaObjectId,
    Metadata,
    OrganisationId,
    Profile,
    QueryPart,
    QueryPartStrategy,
    RecordId,
    RecordProfiles,
    RecordType,
    Restriction,
    ShAbstractHttpService,
    ShCachingService,
    ShPaginationService,
    ZoneRestrictions
} from '@stylehaven/components';
import { ClassificationErrorType } from '../../entities/error/classification-error-type.entity';
import { UserStoreService } from '../user/user-store.service';
import { MediaHavenOrganisationService } from './organisation.service';

@Injectable({ providedIn: 'root' })
export class ClassificationService {
    protected readonly FALLBACK_VALUE: ApiFragment[] = [];
    protected readonly TYPE_NAME: string = 'Classification';

    private SERVICE_NAME = 'ClassificationService';
    private readonly classificationEndpoint: string = `v2/classifications`;
    private readonly recordEndpoint: string = 'v2/records';

    /* README: CURRENT API IMPLEMENTATION [v21.1]

    There are two ways of requesting profiles, but they do NOT return the same data.

    * Profiles as subresource in separate request
    * Profiles in context block

    1. SUBRESOURCE IN SEPARATE REQUEST
        --> /records/:recordId/profiles

        Allow you to set and retrieve arbitrary profileIds on the record.

        * These are NOT inherited (atm)
        * These are NOT filtered on organisation
        * These are NOT filtered on recordtype

        --> This is just RAW data. We will refer to this as SAVED profiles

    2. PROFILES IN CONTEXT BLOCK

        Context : {
            ...,
            Profiles: null | string[]
        }

        These profiles are CALCULATED (even though they may be cached on the object).
        They will take the current record context into account, like user organisation and recordtype.
        The profiles that can be found here are dependent on those SAVED on the parent.


    In the end, the goal is to only having to rely on the Context-Profiles at runtime, and configuring the Saved-profiles at config-time.
    Currently we still have to provide fallbacks in our frontend-middleware to make sure the Context-Profiles are filled in correctly for legacy objects.
    */

    // NOTE: Classifications DO NOT hold profiles, these must be requested separately (see above)

    private organisationService: MediaHavenOrganisationService = inject(MediaHavenOrganisationService);

    constructor(
        protected paginationService: ShPaginationService,
        protected configService: ApplicationConfigService,
        protected http: ShAbstractHttpService,
        private readonly userService: UserStoreService,
        private cachingService: ShCachingService
    ) {
        this.cachingService.register(this.SERVICE_NAME);
    }

    // #region Http methods
    private getClassificationById(classificationId: string): Observable<ApiFragment> {
        if (!classificationId) {
            throw new Error('ClassificationService::getClassificationById No id provided');
        }

        const url = `${this.classificationEndpoint}/${classificationId}`;
        return this.http.get<Metadata>(url).pipe(map(metadata => new ApiFragment(metadata)));
    }

    public getAllClassifications(
        searchTerm: string,
        organisationId?: OrganisationId,
        publishedOnly: boolean = true
    ): Observable<ApiFragment[]> {
        const restriction: Restriction = ZoneRestrictions.fullZoneSearch();

        const queryParts: QueryPart[] = [
            new QueryPart({
                field: 'Title',
                strategy: QueryPartStrategy.MatchAll,
                values: [`*${searchTerm}*`]
            }),
            new QueryPart({
                field: 'Administrative.DeleteStatus',
                strategy: QueryPartStrategy.MatchAll,
                values: [DeleteStatus.NotDeleted]
            })
        ];

        const query: ApiQuery = new ApiQuery(queryParts, restriction);

        const luceneQuery: string = ApiQueryBuilder.queryToLuceneQuery(query);
        let params: HttpParams = new HttpParams({ encoder: new CustomHttpParamEncoder() }).set('q', luceneQuery);

        if (organisationId) {
            params = params.set('organisationId', organisationId);
        }

        params = params.set('published', publishedOnly);

        const request = `${this.classificationEndpoint}?${params.toString()}`;

        return this.paginationService
            .getAll<Metadata>(request)
            .pipe(map(results => results.map(metadata => new ApiFragment(metadata))));
    }

    public getClassificationsByProfileId(
        profileId: string,
        recordTypes: RecordType[],
        organisationId?: OrganisationId
    ): Observable<ApiFragment[]> {
        let restriction: Restriction = ZoneRestrictions.fullZoneSearch();

        const recordTypeList: string[] = recordTypes.length > 0 ? recordTypes : ['All'];
        const recordTypeQueryParts: QueryPart[] = recordTypeList.map(
            recordType =>
                new QueryPart({
                    field: `Internal.Profiles.${recordType}`,
                    strategy: QueryPartStrategy.MatchAtLeastOne,
                    values: [profileId]
                })
        );
        restriction = Combine.And(restriction, Combine.Or(...recordTypeQueryParts));

        const queryParts: QueryPart[] = [];

        const query: ApiQuery = new ApiQuery(queryParts, restriction);

        const luceneQuery: string = ApiQueryBuilder.queryToLuceneQuery(query);
        let params: HttpParams = new HttpParams({ encoder: new CustomHttpParamEncoder() }).set('q', luceneQuery);
        if (organisationId) {
            params = params.set('organisationId', organisationId);
        }

        const request = `${this.classificationEndpoint}?${params.toString()}`;

        return this.paginationService
            .get<Metadata>(request)
            .pipe(map(result => result.Results.map(metadata => new ApiFragment(metadata))));
    }

    private handleRetrieveClassificationFailed(error: ClassificationErrorType): Observable<never> {
        if (error === ClassificationErrorType.DefaultNotConfigured) {
            console.error('No default classification configured');
        } else {
            console.error('Configured default classification could not be found');
        }

        return throwError(error);
    }

    // #endregion

    public getConceptCountByClassification(classificationId: RecordId): Observable<number> {
        const queryParts: QueryPart[] = [
            new QueryPart({
                field: 'ReferenceCodesClassification',
                strategy: QueryPartStrategy.MatchAtLeastOne,
                values: [classificationId]
            })
        ];

        const restriction: Restriction = ZoneRestrictions.fullZoneSearch();

        const luceneQuery: string = ApiQueryBuilder.queryToLuceneQuery(new ApiQuery(queryParts, restriction));

        const params: HttpParams = new HttpParams({ encoder: new CustomHttpParamEncoder() }).set('q', luceneQuery);
        return this.http.count(this.recordEndpoint, params);
    }

    // #region Classification getters
    public get(mediaObjectId: Identifier): Observable<ApiFragment | undefined>;
    public get(mediaObjectIds: Identifier[]): Observable<ApiFragment[]>;
    public get(mediaObjectId: Identifier | Identifier[]): Observable<(ApiFragment | undefined) | ApiFragment[]> {
        if (Array.isArray(mediaObjectId)) {
            return this.getMultiple(mediaObjectId);
        } else {
            return this.getSingle(mediaObjectId);
        }
    }

    private getMultiple(recordIds: Identifier[]): Observable<ApiFragment[]> {
        return forkJoin(recordIds.map(recordId => this.getSingle(recordId))).pipe(
            map(records => records.filter((record): record is ApiFragment => typeof record !== 'undefined'))
        );
    }

    public getSingle(classificationId: Identifier): Observable<ApiFragment | undefined> {
        return this.cachingService.getByIndex(
            this.SERVICE_NAME,
            classificationId,
            this.getClassificationById.bind(this),
            classificationId
        );
    }

    private findByTitle(classificationTitle: string): Observable<ApiFragment | undefined> {
        return this.getAllClassifications(classificationTitle).pipe(
            map(results => (results.length > 0 ? results[0] : undefined))
        );
    }

    public getSingleByTitle(classificationTitle: string): Observable<ApiFragment | undefined> {
        return this.cachingService.getBy(
            this.SERVICE_NAME,
            classificationTitle,
            (classification: ApiFragment) => classification.getTitle(),
            (classification: ApiFragment) => classification.getRecordId(),
            this.findByTitle.bind(this),
            classificationTitle
        );
    }

    // #endregion

    // #region Default and Fallback Classification

    // Default is used for selection options etc
    public getDefaultClassification(): Observable<ApiFragment | null> {
        const userDefaultClassificationId$: Observable<Identifier> = this.userService.getUserPreferences().pipe(
            map(userPreferences => userPreferences.DefaultClassification),
            map(classificationId => {
                if (!classificationId) {
                    throw ClassificationErrorType.DefaultNotConfigured;
                }
                return classificationId;
            })
        );

        return userDefaultClassificationId$.pipe(
            switchMap(userDefaultClassificationId => this.getSingle(userDefaultClassificationId)),
            map(userDefaultClassification => {
                if (!userDefaultClassification) {
                    throw ClassificationErrorType.DefaultNotFound;
                }
                return userDefaultClassification;
            }),
            catchError(() => {
                return this.getFallbackClassification().pipe(
                    catchError(() => {
                        return of(null);
                    })
                );
            }),
            shareReplay(1)
        );
    }

    // Fallback is used for legacy where none is supplied
    public getFallbackClassification(): Observable<ApiFragment> {
        const fallbackClassificationId$: Observable<Identifier> = this.getFallbackClassificationId().pipe(
            map(classificationId => {
                if (!classificationId) {
                    throw ClassificationErrorType.DefaultNotConfigured;
                }
                return classificationId;
            })
        );

        return fallbackClassificationId$.pipe(
            switchMap(fallbackClassificationId => this.getSingle(fallbackClassificationId)),
            map(fallbackClassification => {
                if (!fallbackClassification) {
                    throw ClassificationErrorType.DefaultNotFound;
                }
                return fallbackClassification;
            }),
            catchError(error => this.handleRetrieveClassificationFailed(error)),
            shareReplay(1)
        );
    }

    private getFallbackClassificationId(): Observable<Identifier | null | undefined> {
        return this.userService.getCurrentUser().pipe(
            map(user => user.Organisation.Id),
            switchMap(organisationId => this.organisationService.getDefaultPreferences(organisationId as string)),
            map(prefs => prefs.DefaultClassification)
        );
    }

    // #endregion

    // #region Profiles
    private getProfiles(recordId: Identifier): Observable<RecordProfiles> {
        const request = `${this.classificationEndpoint}/${recordId}/profiles`;
        return this.http.get<RecordProfiles>(request);
    }

    public getClassificationProfileIds(classificationId: MediaObjectId | RecordId): Observable<string[]> {
        return this.getProfiles(classificationId).pipe(
            map(apiProfileIds => apiProfileIds.Profiles),
            shareReplay(1)
        );
    }

    public getFallbackClassificationProfileIds(): Observable<string[]> {
        return this.getFallbackClassification().pipe(
            switchMap(classification => this.getClassificationProfileIds(classification.getMediaObjectId()))
        );
    }

    public linkProfilesToClassification(classificationId: RecordId, profileIds: string[]): Observable<boolean> {
        return this.http
            .put(`${this.classificationEndpoint}/${classificationId}/profiles`, { Profiles: profileIds })
            .pipe(map(() => true));
    }

    public updateMetadataProfile(classificationId: RecordId, profile: Profile): Observable<boolean> {
        return this.http
            .put<void>(`${this.classificationEndpoint}/${classificationId}/profiles/${profile.Id}`, profile)
            .pipe(map(() => true));
    }

    public createMetadataProfile(classificationId: RecordId, profile: Profile): Observable<Profile> {
        return this.http.post<Profile>(`${this.classificationEndpoint}/${classificationId}/profiles/`, profile);
    }

    public deleteMetadataProfile(classificationId: RecordId, profileId: string): Observable<boolean> {
        return this.http
            .delete<void>(`${this.classificationEndpoint}/${classificationId}/profiles/${profileId}`)
            .pipe(map(() => true));
    }

    // #endregion

    // #region Other
    public markClassificationForRefresh(classificationId: RecordId, mustBeNewerThan: Date): boolean {
        return this.cachingService.markIfOutDated(this.SERVICE_NAME, classificationId, mustBeNewerThan);
    }

    public publishChanges(classificationId: RecordId): Observable<string | undefined> {
        return this.http
            .post<ClassificationMetadata>(`${this.classificationEndpoint}/${classificationId}/publish-changes`, {})
            .pipe(
                tap(() => this.cachingService.markDirty(this.SERVICE_NAME, classificationId)),
                map(classification => classification.BatchId)
            );
    }
    // #endregion
}
